import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtMXN, elapsedMin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { googleMapsUrl } from "@/components/MapPreview";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import OrderStateBadge from "@/components/OrderStateBadge";
import { Bike, Phone, MapPin, Clock, ExternalLink, User, Package, Truck } from "lucide-react";
import { toast } from "sonner";

function OrdenCard({ o, children, etiqueta }) {
  return (
    <Card data-testid={`reparto-card-${o.id}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-black text-lg">{o.numero}</div>
          <OrderStateBadge state={o.estado_reparto || "PENDIENTE_PREPARACION"} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
          {o.tipo === "PUBLICO" ? "PEDIDO WEB" : o.tipo === "LLEVAR" ? "PARA LLEVAR" : o.tipo}
          {etiqueta && <span className="ml-2 px-2 py-0.5 rounded-md bg-secondary text-foreground/90 font-bold">{etiqueta}</span>}
        </div>
        <div className="text-sm space-y-1">
          <div className="font-bold">{o.cliente_nombre || "—"}</div>
          {o.cliente_telefono && (
            <a href={`tel:${o.cliente_telefono}`} className="flex items-center gap-2 text-primary"><Phone className="h-3 w-3" />{o.cliente_telefono}</a>
          )}
          {o.cliente_direccion && (
            <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="h-3 w-3 mt-1" /><span>{o.cliente_direccion}</span></div>
          )}
          {o.direccion_origen === "MANUAL" && <div className="text-xs italic text-amber-600">Dirección capturada manualmente por sucursal</div>}
          {o.latitud && o.longitud && (
            <a href={googleMapsUrl(o.latitud, o.longitud)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary text-xs" data-testid={`reparto-maps-${o.id}`}><ExternalLink className="h-3 w-3" />Abrir en Google Maps</a>
          )}
          {o.referencias && <div className="text-xs text-muted-foreground">Ref: {o.referencias}</div>}
          {o.created_by_nombre && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <User className="h-3 w-3" />Registrado por: <span className="font-semibold text-foreground/80">{o.created_by_nombre}</span>
              {o.created_by_role && <span className="text-[10px] uppercase opacity-70">/ {o.created_by_role}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xl font-bold text-primary">{fmtMXN(o.total)}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {elapsedMin(o.listo_reparto_en || o.asignado_en || o.created_at)} min
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function Reparto() {
  const { user } = useAuth();
  const [disponibles, setDisponibles] = useState([]);
  const [mias, setMias] = useState([]);
  const [overview, setOverview] = useState(null);
  const [comentarioOpen, setComentarioOpen] = useState(false);
  const [comentario, setComentario] = useState("");
  const [accion, setAccion] = useState({ ordenId: null, estado: null });

  const isRepartidor = user.role === "REPARTIDOR";
  const isAdminOrCaja = ["ADMIN_SUCURSAL", "CAJA"].includes(user.role);

  const load = async () => {
    try {
      if (isRepartidor) {
        const [d, m] = await Promise.all([
          api.get("/reparto/disponibles"),
          api.get("/repartidor/mis-ordenes"),
        ]);
        setDisponibles(d.data || []);
        setMias(m.data || []);
      } else if (isAdminOrCaja) {
        const r = await api.get("/reparto/overview");
        setOverview(r.data);
      }
    } catch (e) { /* silent retry */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [isRepartidor, isAdminOrCaja]);
  useRealtimeEvents((msg) => { if (msg?.type?.startsWith("orden.")) load(); }, () => load());

  const tomarEnvio = async (orden) => {
    try {
      await api.post(`/ordenes/${orden.id}/tomar-envio`);
      toast.success(`Envío ${orden.numero} asignado a ti`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cambiarEstado = async (orden, estado) => {
    if (estado === "NO_ENTREGADO" || estado === "CANCELADO") {
      setAccion({ ordenId: orden.id, estado });
      setComentario("");
      setComentarioOpen(true);
      return;
    }
    try {
      await api.post(`/ordenes/${orden.id}/estado-reparto`, { estado });
      toast.success("Actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmarComentario = async () => {
    if (!comentario.trim()) return toast.error("Comentario requerido");
    try {
      await api.post(`/ordenes/${accion.ordenId}/estado-reparto`, { estado: accion.estado, comentario: comentario.trim() });
      toast.success("Actualizado");
      setComentarioOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ============ VISTA ADMIN / CAJA ============
  if (isAdminOrCaja) {
    const counts = overview?.counts || {};
    const activas = overview?.activas || [];
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Reparto a domicilio</h2>
          <p className="text-sm text-muted-foreground mt-1">Monitoreo. Los repartidores toman envíos automáticamente cuando están listos.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            ["PENDIENTE_PREPARACION", "Preparando", Clock],
            ["LISTO_PARA_REPARTO", "Listos / Disponibles", Package],
            ["ASIGNADO", "Asignados", User],
            ["EN_CAMINO", "En camino", Truck],
            ["ENTREGADO", "Entregados", Bike],
            ["NO_ENTREGADO", "No entregados", Bike],
            ["CANCELADO", "Cancelados", Bike],
          ].map(([key, label, Icon]) => (
            <Card key={key}>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
                <div className="text-3xl font-black mt-1">{counts[key] || 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div>
          <h3 className="text-lg font-bold mb-3">Activas en reparto</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {activas.length === 0 ? (
              <div className="col-span-full text-center text-muted-foreground py-12">
                <Bike className="h-12 w-12 mx-auto opacity-30 mb-2" />Sin pedidos activos en reparto.
              </div>
            ) : activas.map((o) => (
              <OrdenCard key={o.id} o={o} etiqueta={o.repartidor_nombre ? `→ ${o.repartidor_nombre}` : null} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============ VISTA REPARTIDOR ============
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-black tracking-tight">Reparto</h2>
        <p className="text-sm text-muted-foreground mt-1">Toma envíos disponibles y gestiona tus entregas en curso.</p>
      </div>

      {/* Pedidos disponibles */}
      <section>
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><Package className="h-5 w-5 text-primary" />Pedidos disponibles ({disponibles.length})</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {disponibles.length === 0 ? (
            <div className="col-span-full text-center text-muted-foreground py-12">
              <Package className="h-12 w-12 mx-auto opacity-30 mb-2" />
              No hay pedidos disponibles para reparto.
            </div>
          ) : disponibles.map((o) => (
            <OrdenCard key={o.id} o={o} etiqueta="DISPONIBLE">
              <Button className="w-full h-12 font-bold" onClick={() => tomarEnvio(o)} data-testid={`tomar-envio-${o.id}`}>
                <Truck className="h-4 w-4 mr-2" />Tomar envío
              </Button>
            </OrdenCard>
          ))}
        </div>
      </section>

      {/* Mis entregas */}
      <section>
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><Truck className="h-5 w-5 text-primary" />Mis entregas ({mias.length})</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {mias.length === 0 ? (
            <div className="col-span-full text-center text-muted-foreground py-12">
              <Bike className="h-12 w-12 mx-auto opacity-30 mb-2" />
              No tienes entregas asignadas.
            </div>
          ) : mias.map((o) => (
            <OrdenCard key={o.id} o={o}>
              <div className="space-y-2">
                {(o.estado_reparto === "ASIGNADO") && (
                  <Button className="w-full h-12 font-bold" onClick={() => cambiarEstado(o, "EN_CAMINO")} data-testid={`en-camino-btn-${o.id}`}>
                    <Truck className="h-4 w-4 mr-2" />Marcar en camino
                  </Button>
                )}
                {(o.estado_reparto === "EN_CAMINO") && (
                  <Button className="w-full h-12 font-bold" onClick={() => cambiarEstado(o, "ENTREGADO")} data-testid={`entregado-btn-${o.id}`}>
                    ✓ Marcar entregado
                  </Button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => cambiarEstado(o, "NO_ENTREGADO")} data-testid={`no-entregado-${o.id}`}>No entregado</Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => cambiarEstado(o, "CANCELADO")} data-testid={`cancelado-${o.id}`}>Cancelar</Button>
                </div>
              </div>
            </OrdenCard>
          ))}
        </div>
      </section>

      <Dialog open={comentarioOpen} onOpenChange={setComentarioOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Motivo obligatorio</DialogTitle></DialogHeader>
          <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Indica el motivo…" data-testid="motivo-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setComentarioOpen(false)}>Cancelar</Button>
            <Button onClick={confirmarComentario} data-testid="motivo-confirmar-btn">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
